import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState } from 'recoil';
import dayjs from 'dayjs';
import { Table, Spin, Empty, Tag, Button, Select as AntSelect } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSyncAlt } from '@fortawesome/pro-duotone-svg-icons';
import { Line } from 'react-chartjs-2';
import { notificationsAtom } from '../../../../atoms/Atoms';
import {
  DAYS_WINDOW_OPTIONS,
  getPipelineHealthOverview,
  getPipelineHealthSources
} from '../../../../api/pipelineHealthApi';
import { thousand } from '../../../../functions';
import {
  DOMAIN_COLORS,
  FALLBACK_COLOR,
  formatDomain,
  scoreColor,
  statusToColor
} from '../pipelineHealthUtils';

function ExecutiveOverview() {
  const { t } = useTranslation();
  const [notifications, setNotifications] = useRecoilState(notificationsAtom);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [daysWindow, setDaysWindow] = useState(7);
  const [sources, setSources] = useState([]);
  const [sourceFilter, setSourceFilter] = useState('');

  const pushError = (description) => {
    setNotifications([...notifications, { title: t('error'), description, error: true }]);
  };

  // Sources feed the sourceFilter dropdown; fetched once on mount.
  useEffect(() => {
    getPipelineHealthSources()
      .then((res) => setSources(res?.data?.data ?? []))
      .catch((err) => pushError(err?.response?.data?.message || err.message));
  }, []);

  const load = () => {
    setLoading(true);
    getPipelineHealthOverview(daysWindow, sourceFilter)
      .then((res) => {
        if (res?.data?.success === false) {
          pushError(res?.data?.error || t('error'));
          return;
        }
        setData(res?.data?.data ?? res?.data);
      })
      .catch((err) => {
        pushError(err?.response?.data?.message || err.message);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [daysWindow, sourceFilter]);

  const daysOptions = DAYS_WINDOW_OPTIONS.map((d) => ({
    value: d,
    label: t(`pipeline_health_days_${d}`)
  }));
  const sourceOptions = [
    { value: '', label: t('pipeline_health_all_sources') },
    ...sources?.map((s) => ({ value: s.source, label: formatDomain(s.source) }))
  ];

  // ---- Trend chart data -------------------------------------------------

  const trendChart = useMemo(() => {
    const trend = data?.healthTrend7d || [];
    if (trend.length === 0) return null;
    const allDates = Array.from(new Set(
      trend.flatMap((series) => series.points.map((p) => p.date))
    )).sort();
    const labels = allDates.map((d) => dayjs(d).format('MMM DD'));

    const datasets = trend.map((series) => {
      const byDate = new Map(series.points.map((p) => [p.date, p.avgHealth]));
      return {
        label: formatDomain(series.domain),
        data: allDates.map((d) => (byDate.has(d) ? Number(byDate.get(d)) : null)),
        borderColor: DOMAIN_COLORS[series.domain] || FALLBACK_COLOR,
        backgroundColor: DOMAIN_COLORS[series.domain] || FALLBACK_COLOR,
        tension: 0.25,
        spanGaps: true,
        pointRadius: 3
      };
    });
    return { labels, datasets };
  }, [data]);

  const trendOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    scales: {
      y: { min: 0, max: 100, ticks: { stepSize: 25 } }
    },
    plugins: {
      legend: { position: 'bottom' }
    }
  };

  // ---- Top 5 worst table ------------------------------------------------

  const top5Columns = [
    { title: t('entity'), dataIndex: 'entityName', key: 'entityName' },
    { title: t('domain'), dataIndex: 'domain', key: 'domain', render: formatDomain },
    { title: t('source'), dataIndex: 'source', key: 'source', render: formatDomain },
    {
      title: t('total_errors'),
      dataIndex: 'totalErrors',
      key: 'totalErrors',
      width: 140,
      align: 'center',
      render: thousand
    }
  ];

  // -----------------------------------------------------------------------

  const overall = data?.globalMetrics || {};
  const domainSummary = data?.domainSummary || [];
  const top5 = data?.top5Worst || [];

  return (
    <div className="flex flex-col h-full">
      <div className="flex flex-wrap items-center gap-3 px-2 pb-2">
        <span className="text-sm text-gray-600">{t('pipeline_health_days_window')}:</span>
        <AntSelect
          value={daysWindow}
          disabled={loading}
          onChange={setDaysWindow}
          style={{ width: 150 }}
          options={daysOptions}
        />
        <span className="text-sm text-gray-600">{t('pipeline_health_source_filter')}:</span>
        <AntSelect
          value={sourceFilter}
          disabled={loading}
          onChange={setSourceFilter}
          style={{ minWidth: 160 }}
          options={sourceOptions}
        />
        <Button
          className="ml-auto"
          type="primary"
          onClick={load}
          loading={loading}
          icon={<FontAwesomeIcon icon={faSyncAlt} />}
        >
          {t('refresh')}
        </Button>
      </div>

      {loading && !data ? (
        <div className="flex items-center justify-center py-10"><Spin /></div>
      ) : !data ? (
        <Empty description={t('no_data')} />
      ) : (
        <div className="px-2 pb-4 space-y-4">

          {/* Global metrics */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Spin spinning={loading}>
              <div className="bg-white rounded shadow-sm p-3">
                <div className="text-xs text-gray-500">{t('pipeline_health_dataset_range')}</div>
                <div className="text-base font-semibold">
                  {overall.datasetStart ? dayjs(overall.datasetStart).format('MMM DD, YYYY') : '—'}
                  {' – '}
                  {overall.datasetEnd ? dayjs(overall.datasetEnd).format('MMM DD, YYYY') : '—'}
                </div>
              </div>
            </Spin>
            <Spin spinning={loading}>
              <div className="bg-white rounded shadow-sm p-3">
                <div className="text-xs text-gray-500">{t('pipeline_health_total_entities')}</div>
                <div className="text-2xl font-bold">{thousand(overall.totalEntitiesMonitored ?? 0)}</div>
              </div>
            </Spin>
            <Spin spinning={loading}>
              <div className="bg-white rounded shadow-sm p-3">
                <div className="text-xs text-gray-500">{t('pipeline_health_sla_global')}</div>
                <div className="text-2xl font-bold" style={{ color: scoreColor(overall.slaPercent) }}>
                  {overall.slaPercent ?? 0}%
                </div>
              </div>
            </Spin>
            <Spin spinning={loading}>
              <div className="bg-white rounded shadow-sm p-3">
                <div className="text-xs text-gray-500">{t('pipeline_health_active_domains')}</div>
                <div className="text-2xl font-bold">
                  {overall.activeDomainsToday ?? 0} / {overall.totalDomains ?? 0}
                </div>
              </div>
            </Spin>
          </div>

          {/* Per-domain semaphore cards */}
          <div
            className="grid gap-3"
            style={{ gridTemplateColumns: `repeat(${Math.max(domainSummary.length, 1)}, minmax(140px, 1fr))` }}
          >
            {domainSummary.map((d) => (
              <Spin spinning={loading}>
                <div
                  key={d.domain}
                  className="bg-white rounded shadow-sm p-3 text-center"
                  style={{ borderTop: `4px solid ${DOMAIN_COLORS[d.domain] || FALLBACK_COLOR}` }}
                >
                  <div className="text-xs uppercase tracking-wide text-gray-500">{formatDomain(d.domain)}</div>
                  <div className="text-3xl font-bold" style={{ color: scoreColor(d.healthScore) }}>
                    {d.healthScore}
                  </div>
                  <div className="text-xs text-gray-400">{t('pipeline_health_score')}</div>
                  <div className="flex justify-center gap-2 mt-2 text-xs">
                    <span className="text-green-600">●&nbsp;{thousand(d.okCount)}</span>
                    <span className="text-amber-600">●&nbsp;{thousand(d.warnCount)}</span>
                    <span className="text-red-600">●&nbsp;{thousand(d.critCount)}</span>
                  </div>
                </div>
              </Spin>
            ))}
          </div>

          {/* 7-day trend */}
          <div className="bg-white rounded shadow-sm p-3">
            <div className="text-sm font-semibold mb-2">{t('pipeline_health_trend_7d')}</div>
            {trendChart ? (
              <Spin spinning={loading}>
                <div style={{ height: 240 }}>
                  <Line data={trendChart} options={trendOptions} />
                </div>
              </Spin>
            ) : (
              <Empty description={t('no_data')} />
            )}
          </div>

          {/* Top 5 worst */}
          <div className="bg-white rounded shadow-sm p-3">
            <div className="text-sm font-semibold mb-2">{t('pipeline_health_top_worst')}</div>
            {top5.length > 0 ? (
              <Table
                size="small"
                loading={loading}
                rowKey={(row) => `${row.entityName}-${row.domain}`}
                columns={top5Columns}
                dataSource={top5}
                pagination={false}
              />
            ) : (
              <Empty description={t('no_data')} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ExecutiveOverview;
